import { ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { RouteService } from '../../../service/route.service';
import { GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { DynamicGridComponent } from '../../shared/dynamic-grid/dynamic-grid.component';
import { Subscription } from 'rxjs';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { HttpClient } from '@angular/common/http';
import { GoogleCharts } from 'google-charts';

@Component({
  selector: 'app-pms-agent-health-status',
  templateUrl: './pms-agent-health-status.component.html',
  styleUrl: './pms-agent-health-status.component.scss',
})
export class PmsAgentHealthStatusComponent implements OnDestroy {
  @ViewChild(DynamicGridComponent) dynamicGrid!: DynamicGridComponent;

  private readonly healthStatusFilterOptions = {
    data: [
      { id: 115, name: 'Healthy' },
      { id: 125, name: 'Unhealthy' },
    ],
  };

  // Timeline modal state
  showTimelineModal = false;
  isTimelineLoading = false;
  timelineError = '';
  private timelineChartId = 'agent-timeline-chart';
  private allTimelineData: { practiceCode: string; status: string; startTime: string; endTime: string }[] = [];
  private visualization: any = null;
  timelinePracticeOptions: string[] = [];
  selectedTimelinePractices: Set<string> = new Set();
  practiceFilterSearch = '';
  showPracticeFilterDropdown = false;

  get filteredPracticeOptions(): string[] {
    const q = this.practiceFilterSearch.trim().toLowerCase();
    return q ? this.timelinePracticeOptions.filter(p => p.toLowerCase().includes(q)) : this.timelinePracticeOptions;
  }

  extraActions = [
    {
      label: 'Agent Health Status Timeline',
      cssClass: 'inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-400 text-white regular-font focus:outline-none focus:ring-2 focus:ring-indigo-500',
      action: () => this.openTimelineModal(),
    },
  ];

  gridConfig: GridConfig = {
    columns: [
      {
        label: 'Practice Code',
        jsonKey: 'practiceCode',
        dataType: 'text-wrap',
      },
      { label: 'Live', jsonKey: 'isLive', dataType: 'toggle', toggleUrl: API_ENDPOINTS.AGENT_HEALTH_STATUS + '/:id/toggle-live' },
      {
        label: 'PMS Agent Version',
        jsonKey: 'latestPMSAgentVersion',
        dataType: 'string',
        isSortable: false
      },
      { label: 'PMS Name', jsonKey: 'pmsName', dataType: 'string' },
      {
        label: 'Supported PMS Version',
        jsonKey: 'pmsVersion',
        dataType: 'string',
        isSortable: false,
      },
      {
        label: 'Latest PMS Version',
        jsonKey: 'latestPMSVersion',
        dataType: 'string',
      },
      {
        label: 'PMS SOURCE DATABASE CONNECTIVITY',
        jsonKey: 'isPMSSourceDatabaseConnectionHealthy',
        dataType: 'boolean',
      },
      { label: 'Download Speed (mbps)', jsonKey: 'internetDownloadSpeed', dataType: 'number' },
      { label: 'Upload Speed (mbps)', jsonKey: 'internetUploadSpeed', dataType: 'number' },
      { label: 'Status', jsonKey: 'pmsStatus', dataType: 'string', width: 'full' },
      { label: 'Last Reported', jsonKey: 'createdAt', dataType: 'date' },
    ],
    filters: [
      {
        label: 'Practice',
        jsonKey: 'practice',
        type: 'multi',
        options: [],
      },
      {
        label: 'Health Status',
        jsonKey: 'status',
        type: 'single',
        options: this.healthStatusFilterOptions,
        width: 'w-50',
      },
    ],

    editable: false,
    deletable: false,
    showAllPagination: true,
    uniqueTableName: 'PMSAgentHealthStatusReport',
    rowColorCondition: { color: "RED", pmsStatusCode: 125 }
  };

  ngOnInit() {
    this.fetchFilterOptions();
  }

  fetchFilterOptions() {
    this.fetchPracticeLoadOptions();
  }

  getPracticesUrl = API_ENDPOINTS.GETALLACTIVEPRACTICES;

  fetchPracticeLoadOptions() {
    // Make an API call to fetch practice load options
    this.http
      .get<any>(this.getPracticesUrl)
      .subscribe((response) => {
        response.data = response.data
          .map((option) => ({
            ...option,
            name: option.practiceCode,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        response.data = [{ id: 'All', name: 'All Practices', checked: true }, ...response.data];
        this.gridConfig.filters.find(
          (filter) => filter.jsonKey === 'practice'
        ).options = response;
      });
  }

  getDataUrl = API_ENDPOINTS.AGENT_HEALTH_STATUS;

  optionGetUrlPractice = API_ENDPOINTS.PRACTICES;
  dropDownPracticeColumn = 'name';
  routes: string[];
  private routerSubscription: Subscription;

  constructor(
    private routeService: RouteService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) { }

  //  method to set PMSAgentHealthCheck page as default in roports
  ngAfterViewInit(): void {
    this.routerSubscription = this.routeService
      .getRoutes()
      .subscribe((data) => {
        this.routes = data;
        this.routes.shift();
        if (this.routes.length == 2) {
          this.routeService.redirectTO(
            this.routes.join('/').concat('/pms-agent-health-status')
          );
        }
        this.cdr.detectChanges();
      });
  }

  refreshHandler() {
    this.dynamicGrid.fetchData();
  }

  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
    this.refreshHandler();
  }

  openTimelineModal() {
    this.showTimelineModal = true;
    this.timelineError = '';
    this.isTimelineLoading = false;
    this.allTimelineData = [];
    this.selectedTimelinePractices = new Set();
    this.practiceFilterSearch = '';
    this.showPracticeFilterDropdown = false;

    // Reuse practice list already loaded for the grid filter
    const practiceOptions = this.gridConfig.filters.find(f => f.jsonKey === 'practice')?.options?.data || [];
    this.timelinePracticeOptions = practiceOptions.map((p: any) => p.name).sort();
  }

  closeTimelineModal() {
    this.showTimelineModal = false;
    this.showPracticeFilterDropdown = false;
  }

  toggleTimelinePractice(code: string) {
    if (this.selectedTimelinePractices.has(code)) {
      this.selectedTimelinePractices.delete(code);
    } else {
      this.selectedTimelinePractices.add(code);
    }
  }

  applyPracticeFilter() {
    if (this.selectedTimelinePractices.size === 0) {
      this.showPracticeFilterDropdown = false;
      return;
    }
    this.showPracticeFilterDropdown = false;
    this.isTimelineLoading = true;
    this.timelineError = '';

    const params = Array.from(this.selectedTimelinePractices)
      .map(code => `practiceCodes=${encodeURIComponent(code)}`)
      .join('&');
    const tzOffset = new Date().getTimezoneOffset();

    this.http.get<any>(`${API_ENDPOINTS.AGENT_HEALTH_STATUS_TIMELINE}?${params}&timezoneOffsetMinutes=${tzOffset}`).subscribe({
      next: (response) => {
        this.isTimelineLoading = false;
        this.allTimelineData = response.data || [];
        this.loadAndDraw(this.allTimelineData);
      },
      error: () => {
        this.isTimelineLoading = false;
        this.timelineError = 'Failed to load timeline data.';
      },
    });
  }

  clearPracticeFilter() {
    this.selectedTimelinePractices = new Set();
    this.practiceFilterSearch = '';
    this.showPracticeFilterDropdown = false;
    this.allTimelineData = [];
    const container = document.getElementById(this.timelineChartId);
    if (container) container.innerHTML = '';
  }

  private loadAndDraw(data: { practiceCode: string; status: string; startTime: string; endTime: string }[]) {
    if (this.visualization) {
      setTimeout(() => this.drawTimelineChart(data));
      return;
    }
    GoogleCharts.load(() => {
      const api = GoogleCharts.api;
      if (!api) return;
      api.charts.load('current', { packages: ['timeline'] });
      api.charts.setOnLoadCallback(() => {
        this.visualization = api.visualization;
        setTimeout(() => this.drawTimelineChart(data));
      });
    });
  }

  private drawTimelineChart(data: { practiceCode: string; status: string; startTime: string; endTime: string }[]) {
    const container = document.getElementById(this.timelineChartId);
    if (!container || !this.visualization) return;

    const dataTable = new this.visualization.DataTable();
    dataTable.addColumn({ type: 'string', id: 'Practice' });
    dataTable.addColumn({ type: 'string', id: 'Status' });
    dataTable.addColumn({ type: 'string', role: 'tooltip' });
    dataTable.addColumn({ type: 'date', id: 'Start' });
    dataTable.addColumn({ type: 'date', id: 'End' });

    data.forEach((row) => {
      const start = new Date(row.startTime);
      const end = new Date(row.endTime);
      const durationMin = Math.round((end.getTime() - start.getTime()) / 60000);
      const tooltip = `${row.practiceCode} — ${row.status}\n${start.toLocaleString()} → ${end.toLocaleString()}\nDuration: ${durationMin} min`;
      dataTable.addRow([row.practiceCode, row.status, tooltip, start, end]);
    });

    const chart = new this.visualization.Timeline(container);
    chart.draw(dataTable, {
      height: Math.max(300, data.length * 42 + 60),
      colors: ['#22c55e', '#ef4444'],
      tooltip: { isHtml: false },
      timeline: { showRowLabels: true, colorByRowLabel: false },
    });
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }
}
